import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../lib/api.ts';
import { CampoDoPasso } from '../componentes/PassoAPasso.tsx';

const MINIMO = 12;

type Conferencia = { fase: 'conferindo' } | { fase: 'valido'; nome: string } | { fase: 'invalido' };

/**
 * A tela que abre pelo link do e-mail (`/?redefinir=TOKEN`).
 *
 * Confere o link ANTES de mostrar o formulário: ninguém deve digitar a senha
 * nova duas vezes para só então descobrir que o link venceu.
 */
export function RedefinirSenha({ token, aoTerminar }: { token: string; aoTerminar: () => void }) {
  const [conferencia, setConferencia] = useState<Conferencia>({ fase: 'conferindo' });
  const [nova, setNova] = useState('');
  const [repetida, setRepetida] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const emVoo = useRef(false);

  useEffect(() => {
    api
      .conferirLink(token)
      .then((r) =>
        setConferencia(r.valido && r.nome ? { fase: 'valido', nome: r.nome } : { fase: 'invalido' }),
      )
      .catch(() => setConferencia({ fase: 'invalido' }));
  }, [token]);

  async function enviar(evento: FormEvent) {
    evento.preventDefault();
    if (emVoo.current) return;
    if (nova !== repetida) return setErro('A nova senha e a repetição não são iguais.');
    if (nova.length < MINIMO) {
      return setErro(`A senha precisa ter pelo menos ${MINIMO} caracteres.`);
    }

    emVoo.current = true;
    setSalvando(true);
    setErro(null);
    try {
      await api.redefinirSenha(token, nova);
      setPronto(true);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui redefinir a senha.');
      emVoo.current = false;
      setSalvando(false);
    }
  }

  return (
    <div className="login-fundo">
      <div className="login-marca">
        <img src="/marca/kairos-branco.png" alt="KAIRÓS Asset Intelligence" />
        <span className="marca-hub">Hub</span>
      </div>

      <div className="login-caixa">
        {conferencia.fase === 'conferindo' && <p className="ajuda">Conferindo o link…</p>}

        {conferencia.fase === 'invalido' && (
          <>
            <p className="erro">
              Este link não vale mais. Ele dura 30 minutos e só pode ser usado uma vez.
            </p>
            <button type="button" className="botao-primario login-botao" onClick={aoTerminar}>
              Voltar para a entrada
            </button>
          </>
        )}

        {conferencia.fase === 'valido' && !pronto && (
          <form onSubmit={enviar} noValidate>
            <p className="ajuda ajuda-solta">
              {conferencia.nome}, escolha a sua nova senha.
            </p>
            {erro && <p className="erro">{erro}</p>}

            {/* ⭐ O campo novo — 19/09/2026 (PRD §47.3); os mesmos `id` e `autoComplete`. */}
            <div className="login-campos">
              <CampoDoPasso rotulo="Nova senha" col={6} nota={`Pelo menos ${MINIMO} caracteres.`}>
                <input
                  id="nova"
                  type="password"
                  autoComplete="new-password"
                  value={nova}
                  onChange={(e) => setNova(e.target.value)}
                  autoFocus
                />
              </CampoDoPasso>
              <CampoDoPasso rotulo="Repita a nova senha" col={6}>
                <input
                  id="repetida"
                  type="password"
                  autoComplete="new-password"
                  value={repetida}
                  onChange={(e) => setRepetida(e.target.value)}
                />
              </CampoDoPasso>
            </div>

            <button
              type="submit"
              className="botao-primario login-botao"
              disabled={salvando || !nova || !repetida}
            >
              {salvando ? 'Salvando…' : 'Salvar nova senha'}
            </button>
          </form>
        )}

        {pronto && (
          <>
            <p className="aviso-bom">
              Senha redefinida. Todas as sessões abertas foram encerradas — entre de novo.
            </p>
            <button type="button" className="botao-primario login-botao" onClick={aoTerminar}>
              Entrar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
